import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Wrapper,
  Title,
  SubTitle,
  ResendText,
  Content,
  ResendButton,
  OtpLoaderWrapper,
} from "./style";
import {
  FormWrapper,
  InputAdnLabelWrapper,
  PersonalInfoInput,
  PersonalInfoInputLabel,
} from "../verifyIdentity/styles";
import NavButton from "../../common/button";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getErrorMessage, getErrorCode, onboardingPath } from "../../helpers";
import {
  confirmKeepzOnBoarding,
  beginKeepzOnboarding,
} from "../../../../../API/PIservices";
import { useMutation } from "@tanstack/react-query";
import Error from "../verifyIdentity/Modals/Error";
import useCountdown from "../../../hooks/useCountdown";
import { BANKS, ERROR_CODES } from "../../chooseVerificationMethod/constants";
import { ROUTES } from "../../constants";
import { useNavigateAfterReload } from "../../../../../hooks/useNavigateAfterReload";
import Loader from "../../../../../shared/loaders/simpleLoader";
import PopupLoader from "../../../../../shared/loaders/PopupLoader";

function ConfirmBankVerification({ otpInfo }) {
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState(null);
  const { reset: restFormFields } = useFormContext();

  useNavigateAfterReload();

  const searchParams = new URLSearchParams(window.location.search);
  const bankCode = searchParams.get("bank-code");

  const { t, i18n } = useTranslation();

  const isGeorgian = i18n.language === "ka";

  const { seconds, formatted, reset, setSeconds } = useCountdown(
    Number(otpInfo?.otpResendSeconds)
  );

  const isCountDownOver = Number(seconds) <= 0;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setFocus,
  } = useForm();

  const { isPending, mutate: handleVerifyOtp } = useMutation({
    mutationFn: (data) => {
      return confirmKeepzOnBoarding({ otpCode: data?.otpCode });
    },
    onSuccess: (res) => {
      restFormFields();
      localStorage.setItem("person", JSON.stringify(res?.data));
      history.replace(onboardingPath(ROUTES.privateInformation));
    },
    onError: (err) => {
      setErrorMessage(getErrorMessage(err));
      const errCode = getErrorCode(err);
      if (errCode === ERROR_CODES.reset) {
        setSeconds(0);
        setValue("otpCode", "");
        return;
      }
      if (errCode === ERROR_CODES.redirect) {
        setTimeout(() => {
          history.goBack();
        }, 1500);
      }
    },
  });

  const { isPending: isResendPending, mutate: handleBeginKeepzOnboarding } =
    useMutation({
      mutationFn: (bankCode) => {
        return beginKeepzOnboarding({ bankCode });
      },
      onSuccess: () => {
        reset();
        setValue("otpCode", "");
        setFocus("otpCode");
      },
      onError: (err) => {
        setErrorMessage(getErrorMessage(err));
      },
    });

  useEffect(() => {
    if (!isCountDownOver && otpInfo?.otpResendSeconds) {
      setFocus("otpCode");
    }
  }, [isCountDownOver, otpInfo?.otpResendSeconds, setFocus]);

  function handleResend() {
    if (isPending) return;
    handleBeginKeepzOnboarding(bankCode);
  }

  function onSubmit(data) {
    if (isResendPending) return;
    handleVerifyOtp({ otpCode: data?.otpCode });
  }

  const isButtonDisabled = isPending || isResendPending || isCountDownOver;

  return (
    <Wrapper>
      <Content>
        <Title>{t("confirm_with_code")}</Title>

        <SubTitle>
          {t("code_sens_for_registered_bank", {
            bank: isGeorgian
              ? t(BANKS?.[bankCode]?.["translationKey"])?.replace(/.$/, "ში")
              : t(BANKS?.[bankCode]?.["translationKey"]),
          })}{" "}
        </SubTitle>

        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
          <InputAdnLabelWrapper>
            <PersonalInfoInputLabel htmlFor="otpCode">
              {t("one_time_code")}
            </PersonalInfoInputLabel>
            <PersonalInfoInput
              disabled={
                isPending ||
                isResendPending ||
                isCountDownOver ||
                !otpInfo?.otpResendSeconds
              }
              inputmode="numeric"
              maxlength="6"
              pattern="^\d{1,4}$"
              {...register("otpCode", {
                required: true,
                minLength: {
                  value: 4,
                  message: "Must be at least 4 characters",
                },
              })}
              onInput={(e) => {
                const el = e.currentTarget;
                el.value = el.value.replace(/\D/g, "").slice(0, 4);
              }}
              hasError={errors?.otpCode}
            />
          </InputAdnLabelWrapper>

          {!otpInfo?.otpResendSeconds ? null : !isCountDownOver ? (
            <ResendText>
              {t("resend_code")} {formatted}
            </ResendText>
          ) : isResendPending ? (
            <OtpLoaderWrapper>
              <Loader className="otp-inline-loader" />
              <span>{t("sending") + "..."}</span>
            </OtpLoaderWrapper>
          ) : (
            <ResendButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleResend();
              }}
            >
              {t("resend_code")}
            </ResendButton>
          )}

          <NavButton
            disabled={isButtonDisabled}
            showPrevButton
            nextText={t("confirmation")}
            handlePrev={(e) => {
              if (isPending) return;
              e.stopPropagation();
              history.goBack();
            }}
          />
        </FormWrapper>
      </Content>
      <PopupLoader isOpen={isPending} />
      <Error
        errorMessage={errorMessage}
        close={() => {
          setErrorMessage(null);
        }}
      />
    </Wrapper>
  );
}

export default ConfirmBankVerification;
